import React, { useMemo } from 'react';
import { useCurrency } from '../../context/CurrencyContext';
import { usePortfolioContext } from '../../context/PortfolioContext';
import { coinWalletBalances } from '../Transactions/walletUtils';
import { formatAmount } from '../../utils/formatters';

const BRONZE = '#b89968';

/**
 * Informational "Your holdings" panel for the Split Sell planner. Shows the total
 * owned of the selected coin and a per-wallet breakdown, so the user can see how
 * much they can realistically plan to sell. Display only — it never constrains or
 * validates the sell tiers.
 *
 * Reads from PortfolioContext (the same sample-aware holdings/transactions the
 * Portfolio and Transactions features use), so it works on both the Coin Detail
 * page and the Strategy tab, and populates correctly in sample mode.
 */
export function SellHoldingsPanel({ coinId, symbol }: { coinId: string; symbol: string }) {
  const { fmt } = useCurrency();
  const { holdings, transactions } = usePortfolioContext();

  const holding = holdings.find((h) => h.coinId === coinId);
  const totalQty = holding?.amount ?? 0;
  const priceUsd = holding?.currentPrice ?? 0;
  const totalValueUsd = holding?.totalValue ?? 0;

  const wallets = useMemo(() => coinWalletBalances(coinId, transactions), [coinId, transactions]);
  const hasHoldings = totalQty > 1e-9;

  const chipStyle: React.CSSProperties = {
    background: 'rgb(var(--overlay) / 0.04)',
    border: '0.5px solid rgb(var(--overlay) / 0.08)',
    color: 'var(--color-sub)',
  };

  return (
    <div
      style={{
        background: 'rgb(var(--overlay) / 0.02)',
        border: '0.5px solid rgb(var(--overlay) / 0.05)',
        borderRadius: 10,
        padding: 12,
      }}
    >
      <p
        className="uppercase font-semibold mb-2"
        style={{ fontSize: 10, letterSpacing: '0.1em', color: BRONZE }}
      >
        Your holdings
      </p>

      {hasHoldings ? (
        <>
          {/* Headline — total across all wallets + current value */}
          <div className="flex items-baseline gap-2 flex-wrap mb-2.5">
            <span className="num text-[15px] font-medium" style={{ color: 'rgb(var(--sl-100))' }}>
              {formatAmount(totalQty)} {symbol}
            </span>
            <span className="num text-[12px]" style={{ color: 'var(--color-sub)' }}>
              ≈ {fmt(totalValueUsd)}
            </span>
          </div>

          {/* Per-wallet breakdown */}
          <div className="flex flex-col gap-1">
            {wallets.map((w) => (
              <div key={w.wallet} className="flex items-center justify-between gap-2">
                <span
                  className="inline-block text-[10px] px-1.5 py-0.5 rounded-md font-medium whitespace-nowrap flex-shrink-0"
                  style={chipStyle}
                >
                  {w.wallet}
                </span>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="num text-[12px]" style={{ color: 'rgb(var(--sl-100))' }}>
                    {formatAmount(w.balance)} {symbol}
                  </span>
                  <span className="num text-[12px] text-right min-w-[64px]" style={{ color: 'var(--color-sub)' }}>
                    {fmt(w.balance * priceUsd)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <p className="text-[12px]" style={{ color: 'var(--color-sub)' }}>
          You don&apos;t currently hold any {symbol}. You can still plan a hypothetical sell below.
        </p>
      )}
    </div>
  );
}
